import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ApiService } from '../api.service';
import { SubscriptionSink } from '@app/shared';

@Component({
  selector: 'app-bug-report-modal',
  templateUrl: './bug-report-modal.component.html',
  styleUrls: ['./bug-report-modal.component.scss']
})
export class BugReportModalComponent implements OnInit, OnDestroy {

  form: FormGroup;
  isVisible = false;
  emailSent = false;
  emailError = false;
  isLoading = false;
  selectedFiles: File[] = [];

  protected subscription = new SubscriptionSink();

  constructor(protected fb: FormBuilder, protected api: ApiService) { }

  ngOnInit() {
    this.form = this.fb.group({
      email: this.fb.control(null, [Validators.required, Validators.email]),
      subject: this.fb.control(null, [Validators.required]),
      description: this.fb.control(null, [Validators.required]),
      attachments: this.fb.control(null)
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  open() {
    this.isVisible = true;
    this.emailSent = false;
    this.emailError = false;
    this.selectedFiles = [];
    this.form.reset();
    
    // Reset textarea height when modal opens
    setTimeout(() => {
      const textarea = document.getElementById('description') as HTMLTextAreaElement;
      if (textarea) {
        textarea.style.height = '100px';
      }
    }, 0);
  }

  close() {
    this.isVisible = false;
  }

  submit() {
    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;
    this.emailSent = false;
    this.emailError = false;

    // Process attachments to convert File objects to the expected format
    this.processAttachments().then(processedAttachments => {
      const bugReportData = {
        email: this.form.value.email,
        subject: this.form.value.subject,
        description: this.form.value.description,
        attachments: processedAttachments,
      };

      this.subscription.sink = this.api.userSendBugReport(bugReportData)
        .subscribe(
          () => {
            this.emailSent = true;
            this.emailError = false;
            this.isLoading = false;
            setTimeout(() => this.close(), 2000); // Auto close after 2 seconds
          },
          () => {
            this.emailError = true;
            this.emailSent = false;
            this.isLoading = false;
          }
        );
    }).catch(() => {
      this.emailError = true;
      this.emailSent = false;
      this.isLoading = false;
    });
  }

  onFileSelected(event: any) {
    const newFiles = Array.from(event.target.files) as File[];
    
    // Filter out files that are already selected (check by name and size to avoid duplicates)
    const uniqueNewFiles = newFiles.filter(newFile => 
      !this.selectedFiles.some(existingFile => 
        existingFile.name === newFile.name && existingFile.size === newFile.size
      )
    );
    
    // Add new unique files to existing ones
    this.selectedFiles = [...this.selectedFiles, ...uniqueNewFiles];
    this.form.patchValue({ attachments: this.selectedFiles });
    
    // Clear the input to allow selecting the same file again
    event.target.value = '';
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.form.patchValue({ attachments: this.selectedFiles });
  }

  getFileNames(): string {
    return this.selectedFiles.map(file => file.name).join(', ');
  }

  autoResize(event: any) {
    const textarea = event.target;
    
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate the new height based on content
    const newHeight = Math.max(textarea.scrollHeight, 100);
    
    // Set the new height
    textarea.style.height = newHeight + 'px';
    
    // Ensure the textarea doesn't shrink below minimum
    if (newHeight < 100) {
      textarea.style.height = '100px';
    }
  }

  async processAttachments(): Promise<{filename: string, mimeType: string, content: number[]}[]> {
    const processedAttachments: {filename: string, mimeType: string, content: number[]}[] = [];
    
    for (const file of this.selectedFiles) {
      try {
        // Convert File to base64 or ArrayBuffer
        const fileContent = await this.fileToArrayBuffer(file);
        
        processedAttachments.push({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          content: Array.from(new Uint8Array(fileContent)) // Convert to byte array
        });
      } catch (error) {
        console.error('Error processing file:', file.name, error);
        // Continue with other files even if one fails
      }
    }
    
    return processedAttachments;
  }

  private fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }
}
